import React, { useCallback, useEffect } from "react";
import {
    FormControl, FormHelperText, Grid, Input, MenuItem,
    Select, RadioGroup, FormControlLabel, Radio, Box, CircularProgress
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { GamesType, IGame, IPackageShort } from "../../../../interfaces";
import { PackageRow } from "./package-row";
import { usePackagesCatalog } from "../../../../hooks/usePackagesCatalog";

type PacketsProps = {
    isInherited: boolean;
    setIsInherited: (val: boolean) => void;
    packagesSelected: number[];
    setPackagesSelected: (packagesSelected: number[]) => void;
    gamesSelected: GamesType[];
    setGamesSelected: (gamesSelected: GamesType[]) => void;
    getGamesId: (games: IGame[]) => number[];
}

const Packets: React.FC<PacketsProps> = ({
    isInherited,
    setIsInherited,
    packagesSelected,
    setPackagesSelected,
    gamesSelected,
    setGamesSelected
}) => {
    const [t] = useTranslation();

    const { packages, isLoading, hasMore, loadMore } = usePackagesCatalog();

    useEffect(() => {
        if (!isLoading && hasMore && packagesSelected.length > 0) {
            const hasMissingPackages = packagesSelected.some(
                (selectedId) => !packages.find((p) => p.id === selectedId)
            );

            if (hasMissingPackages) {
                loadMore();
            }
        }
    }, [isLoading, hasMore, packagesSelected, packages, loadMore]);

    const togglePackageSelection = useCallback((pkg: IPackageShort) => {
        const isSelected = packagesSelected.includes(pkg.id);
        if (isSelected) {
            setPackagesSelected(packagesSelected.filter(id => id !== pkg.id));
            setGamesSelected(gamesSelected.filter(g => g.package_id !== pkg.id));
        } else {
            setPackagesSelected([...packagesSelected, pkg.id]);
            setGamesSelected([...gamesSelected, {
                package_id: pkg.id,
                mode: "ALL_PROVIDER",
                games: [],
            }]);
        }
    }, [packagesSelected, gamesSelected, setPackagesSelected, setGamesSelected]);

    const updateGameSelection = useCallback((updatedItem: GamesType) => {
        setGamesSelected(gamesSelected.map(item =>
            item.package_id === updatedItem.package_id ? updatedItem : item
        ));
    }, [gamesSelected, setGamesSelected]);

    const removePackage = useCallback((packageId: number) => {
        setPackagesSelected(packagesSelected.filter(id => id !== packageId));
        setGamesSelected(gamesSelected.filter(item => item.package_id !== packageId));
    }, [packagesSelected, gamesSelected, setPackagesSelected, setGamesSelected]);

    const handleMenuScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const listboxNode = event.currentTarget;
        const scrollTop = listboxNode.scrollTop;
        const clientHeight = listboxNode.clientHeight;
        const scrollHeight = listboxNode.scrollHeight;

        // Запас в 30px для плавной подгрузки
        const isBottom = scrollTop + clientHeight >= scrollHeight - 30;

        if (isBottom && hasMore && !isLoading) {
            loadMore();
        }
    }, [hasMore, isLoading, loadMore]);

    return (
        <Grid item xs={12} sm={12}>
            <FormControl fullWidth size="small">
                <Box mb={2}>
                    <RadioGroup
                        row
                        value={isInherited ? "INHERIT" : "CUSTOM"}
                        onChange={(e) => setIsInherited(e.target.value === "INHERIT")}
                    >
                        <FormControlLabel value="INHERIT" control={<Radio />} label={t("Наследовать настройки")} />
                        <FormControlLabel value="CUSTOM" control={<Radio />} label={t("Настроить список вручную")} />
                    </RadioGroup>
                </Box>

                {!isInherited && (
                    <React.Fragment>
                        <FormHelperText>{t("ru.tables.form_helpers.packets")}</FormHelperText>
                        <Select
                            multiple
                            value={packagesSelected}
                            input={<Input />}
                            MenuProps={{
                                // Отключаем центрирование по выбранному элементу
                                getContentAnchorEl: null,

                                // Жестко привязываем верх меню к низу инпута
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },

                                PaperProps: {
                                    onScrollCapture: handleMenuScroll,
                                    style: {
                                        maxHeight: 300,
                                        overflowY: "auto"
                                    }
                                }
                            }}
                        >
                            {packages.map((item: IPackageShort) => (
                                <MenuItem key={item.id} value={item.id} onClick={() => togglePackageSelection(item)}>
                                    {item.name}
                                </MenuItem>
                            ))}

                            {/* Спиннер загрузки */}
                            {isLoading && (
                                <MenuItem disabled style={{ justifyContent: "center", opacity: 1 }}>
                                    <CircularProgress size={24} />
                                </MenuItem>
                            )}
                        </Select>

                        <Box mt={2}>
                            {packagesSelected.map((packageId) => {
                                const packageItem = packages.find(p => p.id === packageId);
                                const selection = gamesSelected.find(g => g.package_id === packageId);

                                if (!packageItem || !selection) {
                                    return (
                                        <Box key={packageId} display="flex" p={2} justifyContent="center">
                                            <CircularProgress size={24} />
                                        </Box>
                                    );
                                }

                                return (
                                    <PackageRow
                                        key={packageId}
                                        packageItem={packageItem}
                                        selection={selection}
                                        onUpdate={updateGameSelection}
                                        onRemove={removePackage}
                                    />
                                );
                            })}
                        </Box>
                    </React.Fragment>
                )}
            </FormControl>
        </Grid>
    );
};

export default React.memo(Packets);